import React from 'react';
import styled from 'styled-components';

import Section, { Overline } from '../layouts/Section';
import { Grid, Cell } from '../layouts/Grid';

import Text from '../Text';
import HTMLWrapper from '../HTMLWrapper';
import Button from '@basic/Button';
import Video from '@basic/WistiaVideo';

import BasicPlayButton from '@basic/buttons/BasicPlayButton';

import theme from '../../../styles/theme';
import Link from '../../util/link';

const HeroImage = styled.img(
	props => `
        width: 100%;
        ${props.maxWidth ? `max-width: ${props.maxWidth}px;` : ``}
`
);

const TextCell = styled(Cell)`
	margin-bottom: 30px;

	${theme.breakPoints.screenQuery.extraMobile} {
		.grid-container {
			display: block;
		}
	}

	.image-hero__subheader {
		font-size: 20px;
		line-height: 36px; /* 180% */
		letter-spacing: 0.2px;
		font-family: ${theme.font.regular};
	}

	${theme.breakPoints.mobileFirstScreenQuery.large} {
		margin: 0;
	}
`;

const ButtonWrapper = styled.div`
	margin-top: 20px;
	margin-left: auto;
	margin-right: auto;
	display: grid;
	grid-template-columns: repeat(2, auto);
	grid-column-gap: 24px;
	width: fit-content;

	a {
		margin: 10px 0;
	}
	${theme.breakPoints.mobileFirstScreenQuery.large} {
		margin: 0;

		grid-column-gap: 24px;
		grid-row-gap: 22px;
		justify-content: flex-start;
	}
`;

const AdditionalComponent = styled.div`
	hr {
		background: rgba(138, 146, 162, 0.45);
		width: 100%;
		margin: 40px 0;
		opacity: 1;
	}
`;

const HeroLink = styled(Link)`
	position: relative;

	.play-button__wrapper {
		z-index: 1;
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
	}
	:hover {
		.play-button__wrapper {
			background-color: ${theme.colors.primary.dark};
		}
	}
`;

const VideoWrapper = styled.div`
	position: relative;

	.play-button__wrapper {
		z-index: 1;
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
	}

	:hover {
		.play-button__wrapper {
			background-color: ${theme.colors.primary.dark};
		}
	}
`;

const TwoColumnHero = ({
	className,
	overline,
	header,
	subheader,
	buttons,
	heroVideo,
	heroVideoSettings,
	heroImage,
	linkHero,
	withPlayButton,
	additionalComponent,
	lightBox,
	noAutoplay,
	thumbnail,
	...props
}) => {
	return (
		<Section className={className} {...props}>
			<Grid>
				<TextCell span={6} className="image-hero__image-cell vertical-center">
					<Overline className="image-hero__hero-text--overline">
						{overline}
					</Overline>
					<Text component="hero">{header}</Text>
					<HTMLWrapper
						className="image-hero__subheader"
						html={subheader.childMarkdownRemark.html}
					/>
					<ButtonWrapper>
						{buttons.map((button, index) => {
							return (
								<Button
									style={{ marginTop: '10px' }}
									secondary={index === 1}
									link={button.linkUrl}
								>
									{button.linkText}
								</Button>
							);
						})}
					</ButtonWrapper>
					{additionalComponent && (
						<AdditionalComponent>
							<hr />
							{additionalComponent}
						</AdditionalComponent>
					)}
				</TextCell>
				<Cell span={6}>
					{heroVideo && (
						<VideoWrapper>
							<Video
								videoId={heroVideo.slice(
									heroVideo.lastIndexOf('/') + 1,
									heroVideo.length
								)}
								className="image-hero__video"
								noLightbox={!lightBox}
								noPlayButton={!withPlayButton}
								autoPlay={!noAutoplay}
								endVideoBehavior={'loop'}
								muted
								thumbnail={thumbnail && thumbnail.file.url}
								customPadding={props.customPadding}
							/>
						</VideoWrapper>
					)}
					{heroImage && (
						<HeroImage
							className="hero-image"
							src={heroImage}
							alt="Hero Image"
						/>
					)}
					{linkHero && (
						<HeroLink to={linkHero.imageUrl}>
							{withPlayButton && <BasicPlayButton width={60} radius={100} />}
							<HeroImage
								className="hero-image"
								src={linkHero.image.file.url}
								alt={linkHero.image.description || 'Hero Image'}
							/>
						</HeroLink>
					)}
				</Cell>
			</Grid>
		</Section>
	);
};

export default TwoColumnHero;
